import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi } from '../services/userApi';
import Avatar from './Avatar';
import {
  IoSearchOutline,
  IoEyeOutline,
  IoCreateOutline,
  IoTrashOutline,
  IoPeopleOutline
} from 'react-icons/io5';
import './AdminDashboard.css';

function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const limit = 10;

  useEffect(() => {
    loadUsers();
  }, [page, search]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await userApi.getUsers({ limit, offset: (page - 1) * limit, search });
      if (response.success) {
        setUsers(response.data?.data || []);
        setTotal(response.data?.total || 0);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;
    setDeleting(true);
    try {
      // API delete would go here
      setDeleteUserId(null);
      loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return dateString;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="admin-users">
      <div className="admin-users-header">
        <div className="admin-search-box">
          <IoSearchOutline />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Groups</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="admin-spinner"></div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="6">
                  <div className="admin-empty-state">
                    <IoPeopleOutline className="admin-empty-icon" />
                    <div className="admin-empty-title">No users found</div>
                    <div className="admin-empty-text">Users will appear here when accounts are created.</div>
                  </div>
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id} className="admin-user-row">
                  <td onClick={() => navigate(`/admin/users/${user.id}`)} style={{ cursor: 'pointer' }}>
                    <div className="admin-table-user">
                      <Avatar user={user} size="small" />
                      <div>
                        <div className="admin-table-user-name">{user.display_name || user.username}</div>
                        <div className="admin-table-user-email">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <div className="admin-user-groups">
                      {user.groups && user.groups.length > 0 ? (
                        user.groups.map((group, idx) => (
                          <span key={idx} className="admin-group-tag">{group}</span>
                        ))
                      ) : (
                        <span className="admin-group-tag">-</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`admin-status-badge ${user.is_admin ? 'admin' : 'active'}`}>
                      {user.is_admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="admin-action-btn"
                        title="View user"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        <IoEyeOutline />
                      </button>
                      <button
                        className="admin-action-btn"
                        title="Edit user"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        <IoCreateOutline />
                      </button>
                      <button
                        className="admin-action-btn danger"
                        title="Delete user"
                        onClick={() => setDeleteUserId(user.id)}
                      >
                        <IoTrashOutline />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <span className="admin-pagination-info">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
            </span>
            <div className="admin-pagination-buttons">
              <button
                className="admin-pagination-btn"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    className={`admin-pagination-btn ${page === pageNum ? 'active' : ''}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                className="admin-pagination-btn"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteUserId && (
        <div className="admin-modal-overlay" onClick={() => setDeleteUserId(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Delete User</h3>
              <button className="admin-modal-close" onClick={() => setDeleteUserId(null)}>×</button>
            </div>
            <div className="admin-modal-body">
              <p>Are you sure you want to delete this user?</p>
              <p className="admin-delete-warning">This action cannot be undone.</p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn secondary" onClick={() => setDeleteUserId(null)}>Cancel</button>
              <button className="admin-btn danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
